import { Button, FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import React, { type ReactNode, useCallback, useMemo } from 'react';

import {
  auditLogsApi,
  AutomationEventTrigger,
  ConversationAutomationAuditLogEntry,
  ConversationAuditLogEntry,
} from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint/useLazyPaginatedEndpoint';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from '../activityLog.module.scss';

import { LogEntry } from './logEntry';
import { NodeErrors } from './nodeErrors';
import { RoutedThroughAiAgent } from './routedThroughAutomationNodes/components/aiAgent';
import { RoutedThroughAutomationAiAgent } from './routedThroughAutomationNodes/components/automationAiAgent';
import { RoutedThroughBusinessHours } from './routedThroughAutomationNodes/components/businessHours';
import { RoutedThroughChatbotSingleInteraction } from './routedThroughAutomationNodes/components/chatbotSingleInteraction';
import { RoutedThroughClose } from './routedThroughAutomationNodes/components/close';
import { RoutedThroughCondition } from './routedThroughAutomationNodes/components/condition';
import { RoutedThroughCustomAttribute } from './routedThroughAutomationNodes/components/customAttributes';
import { RoutedThroughFinish } from './routedThroughAutomationNodes/components/finish';
import { RoutedThroughIntegration } from './routedThroughAutomationNodes/components/integration';
import { RoutedThroughInternalNote } from './routedThroughAutomationNodes/components/internalNote';
import { RoutedThroughQueue } from './routedThroughAutomationNodes/components/queue';
import { RoutedThroughReply } from './routedThroughAutomationNodes/components/reply';
import { RoutedThroughSideConversation } from './routedThroughAutomationNodes/components/sideConversation';
import { RoutedThroughStop } from './routedThroughAutomationNodes/components/stop';
import { RoutedThroughTag } from './routedThroughAutomationNodes/components/tag';
import { RoutedThroughThirdPartyIntegration } from './routedThroughAutomationNodes/components/thirdPartyIntegration';
import { RoutedThroughWait } from './routedThroughAutomationNodes/components/wait';

type RoutedThroughAutomationProps = {
  readonly entry: ConversationAuditLogEntry<'AUTOMATION_STARTED'>;
};

export const getRoutedNode = (
  node: ConversationAutomationAuditLogEntry,
): JSX.Element => {
  switch (node.activity_type) {
    case 'AUTOMATION_CONDITION_NODE_VISITED':
      return <RoutedThroughCondition node={node} />;
    case 'AUTOMATION_WAIT_NODE_VISITED':
      return <RoutedThroughWait node={node} />;
    case 'AUTOMATION_QUEUE_NODE_VISITED':
      return <RoutedThroughQueue node={node} />;
    case 'AUTOMATION_TAG_NODE_VISITED':
      return <RoutedThroughTag node={node} />;
    case 'AUTOMATION_BUSINESS_HOURS_NODE_VISITED':
      return <RoutedThroughBusinessHours node={node} />;
    case 'AUTOMATION_REPLY_NODE_VISITED':
      return <RoutedThroughReply />;
    case 'AUTOMATION_CLOSE_NODE_VISITED':
      return <RoutedThroughClose />;
    case 'AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED':
      return <RoutedThroughIntegration node={node} />;
    case 'AUTOMATION_STOP_NODE_VISITED':
      return <RoutedThroughStop />;
    case 'AUTOMATION_CONTACT_CUSTOM_FIELDS_NODE_VISITED':
    case 'AUTOMATION_CONVERSATION_CUSTOM_FIELDS_NODE_VISITED':
      return <RoutedThroughCustomAttribute node={node} />;
    case 'AUTOMATION_FINISHED':
      return <RoutedThroughFinish />;
    case 'AUTOMATION_SIDE_CONVERSATION_NODE_VISITED':
      return <RoutedThroughSideConversation node={node} />;
    case 'AUTOMATION_THIRD_PARTY_INTEGRATION_NODE_VISITED':
      return <RoutedThroughThirdPartyIntegration node={node} />;
    case 'AUTOMATION_INTERNAL_NOTE_NODE_VISITED':
      return <RoutedThroughInternalNote node={node} />;
    case 'AUTOMATION_CHATBOT_NODE_VISITED':
      return <RoutedThroughAiAgent node={node} />;
    case 'AUTOMATION_AI_AGENT_NODE_VISITED':
      return <RoutedThroughAutomationAiAgent node={node} />;
    case 'AUTOMATION_CHATBOT_SINGLE_INTERACTION_NODE_VISITED':
      return <RoutedThroughChatbotSingleInteraction node={node} />;
    default:
      return <Text.Paragraph>Unrecognized action</Text.Paragraph>;
  }
};

const groupedNodeTypes = new Set<
  ConversationAutomationAuditLogEntry['activity_type']
>([
  'AUTOMATION_CHATBOT_NODE_VISITED',
  'AUTOMATION_AI_AGENT_NODE_VISITED',
  'AUTOMATION_CHATBOT_SINGLE_INTERACTION_NODE_VISITED',
]);

/**
 * Renders a group of consecutive automation activities with the same type. For
 * chatbot nodes, only the last node is rendered (to avoid duplicate entries).
 */
const getNodeExpressionErrors = (node: ConversationAutomationAuditLogEntry) =>
  'automation_node_attributes' in node
    ? node.automation_node_attributes.expression_errors
    : undefined;

const getNodeScopeVariableSaveErrors = (
  node: ConversationAutomationAuditLogEntry,
) =>
  'automation_node_attributes' in node
    ? node.automation_node_attributes.scope_variable_save_errors
    : undefined;

function getRoutedGroupedNodes(
  nodes: ConversationAutomationAuditLogEntry[],
): ReactNode {
  if (nodes.length === 0) {
    return (
      <Text.Paragraph variant="2" subdued>
        Unrecognized action
      </Text.Paragraph>
    );
  }

  const lastNode = nodes[nodes.length - 1];

  if (groupedNodeTypes.has(lastNode.activity_type)) {
    return (
      <>
        {getRoutedNode(lastNode)}
        <NodeErrors
          expressionErrors={getNodeExpressionErrors(lastNode)}
          scopeVariableSaveErrors={getNodeScopeVariableSaveErrors(lastNode)}
        />
      </>
    );
  }

  return nodes.map((node, index) => (
    <React.Fragment key={index}>
      <Text.Paragraph variant="2" subdued>
        {getRoutedNode(node)}
      </Text.Paragraph>
      <NodeErrors
        expressionErrors={getNodeExpressionErrors(node)}
        scopeVariableSaveErrors={getNodeScopeVariableSaveErrors(node)}
      />
    </React.Fragment>
  ));
}

const getTriggerType = (
  trigger: AutomationEventTrigger,
): JSX.Element | null => {
  const { type } = trigger;

  if (type === 'message_added') {
    return (
      <>{`Triggered by: Message added by ${trigger.message_added?.sender}`}</>
    );
  }

  if (type === 'added_to_queue') {
    return <>Triggered by: Conversation added to queue</>;
  }

  if (type === 'assigned') {
    return <>Triggered by: Conversation assigned to agent</>;
  }

  if (type === 'abandoned') {
    return <>Triggered by: Conversation abandoned</>;
  }

  if (type === 'closed') {
    return <>Triggered by: Conversation closed</>;
  }

  if (type === 'tagged') {
    return <>Triggered by: Tag added</>;
  }

  if (type === 'untagged') {
    return <>Triggered by: Tag removed</>;
  }

  if (type === 'custom_fields_updated') {
    return <>Triggered by: Conversation custom attribute updated</>;
  }

  if (type === 'first_message_added') {
    return <>Triggered by: Conversation created</>;
  }

  if (type === 'sla_breached') {
    return <>Triggered by: SLA breached</>;
  }

  return null;
};

export const RoutedThroughAutomation = ({
  entry,
}: RoutedThroughAutomationProps): JSX.Element | null => {
  const { execution_id: executionId, automation_name: automationName } = entry;

  const navigate = useAppNavigate();

  const {
    items: automationActivities,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readConversationAutomationAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  // Only fetch the automation logs the first time the user expands the
  // section, to avoid firing a request for every automation entry in the log.
  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded && executionId && asyncStatus === 'uninitialized') {
        changeArg({
          resourceId: executionId,
          limit: 1000,
          sort: { key: 'timestamp', direction: 'asc' },
        });
      }
    },
    [asyncStatus, executionId, changeArg],
  );

  const hasAutomationReadAccess = useHasAccess('settings.flow:r');

  /**
   * Groups consecutive automation activities by their activity_type. This
   * allows us to render only the last chatbot node when there are multiple
   * consecutive chatbot visits.
   */
  const groupedActivities = useMemo(
    () =>
      automationActivities.reduce(
        (acc: ConversationAutomationAuditLogEntry[][], node) => {
          const lastGroup = acc[acc.length - 1];

          if (lastGroup && lastGroup[0].activity_type === node.activity_type) {
            lastGroup.push(node);
          } else {
            acc.push([node]);
          }

          return acc;
        },
        [],
      ),
    [automationActivities],
  );

  const handleGoToAutomation = useCallback(() => {
    navigate({
      path: '/settings/automations/flow/:automationId',
      params: {
        path: {
          automationId: entry.rule_id,
        },
      },
    });
  }, [entry.rule_id, navigate]);

  if (!executionId) {
    return null;
  }

  return (
    <LogEntry
      entry={entry}
      icon="route"
      text={`Routed through automation${
        automationName ? `: ${automationName}` : ''
      }`}
      isExpandable
      onExpandedChange={handleExpandedChange}
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {entry.event_trigger && (
          <Text.Paragraph variant="2">
            {getTriggerType(entry.event_trigger)}
          </Text.Paragraph>
        )}
        {asyncStatus === 'rejected' && (
          <span className={styles.error}>
            Failed to retrieve automation activity log.
          </span>
        )}

        {/* Already-fetched items stay visible while the next page loads. */}
        {groupedActivities.map((group, index) => (
          <React.Fragment key={index}>
            {getRoutedGroupedNodes(group)}
          </React.Fragment>
        ))}

        {hasAutomationReadAccess && (
          <FlexBox margin="300 0">
            <Button variant="secondary" onClick={handleGoToAutomation}>
              Go to automation
            </Button>
          </FlexBox>
        )}

        {asyncStatus === 'pending' && !groupedActivities.length && (
          <div className={styles.spinnerContainer} data-testid="spinner">
            <Spinner />
          </div>
        )}

        {groupedActivities.length > 0 && hasNextPage && (
          <FlexBox margin="200 0">
            <Button
              variant="secondary"
              isLoading={asyncStatus === 'pending'}
              onClick={nextPage}
            >
              Load more
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
